<!--
  Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
  This information is confidential and proprietary to GM Global Technology
  Operations LLC and may not be used, modified, copied or distributed.
  -->
@if (permission()) {
<div class="container">
  <h2>URL Request Activity Report</h2>
  <div class="toolbar">
    <label for="reportType">Report:</label>
    <select id="reportType" [(ngModel)]="reportType" (change)="onReportTypeChange()">
      <option value="Grid">Grid</option>
      <option value="Group by Day">Group by Day</option>
      <option value="Group by Hour">Group by Hour</option>
      <option value="Group by Minute">Group by Minute</option>
    </select>
  </div>

  @if (loading) {
    <div class="loading">Loading...</div>
  }

  <!-- Grid View -->
  @if (!loading && reportType === 'Grid') {
    <div class="grid-container">
      <ag-grid-angular
        style="width: 100%; height: 80vh;"
        class="ag-theme-balham"
        [rowHeight]="35"
        [columnDefs]="gridColumnDefs"
        [rowData]="gridData"
        [pagination]="true"
        [paginationPageSize]="100"
        [paginationPageSizeSelector]="[50, 100, 200, 500, 1000]"
        [defaultColDef]="defaultColDef"
        [enableCellTextSelection]="true"
        [suppressRowClickSelection]="true">
      </ag-grid-angular>
    </div>
  }

  <!-- Chart View -->
  @if (!loading && reportType !== 'Grid' && (dataPoints.length === 0 && lineSegments.length === 0)) {
    <div class="no-data">
      No data available
    </div>
  }

  @if (!loading && reportType !== 'Grid' && (dataPoints.length > 0 || lineSegments.length > 0)) {
    <div class="chart-container">
      <svg [attr.width]="width" [attr.height]="height">
        <!-- Draw axes -->
        <line [attr.x1]="padding"
          [attr.y1]="height - padding"
          [attr.x2]="padding + chartWidth"
          [attr.y2]="height - padding"
          stroke="black"
        stroke-width="2"></line>
        <line [attr.x1]="padding"
          [attr.y1]="padding"
          [attr.x2]="padding"
          [attr.y2]="height - padding"
          stroke="black"
        stroke-width="2"></line>
        <!-- Draw boundary line for connection column -->
        <line [attr.x1]="padding + chartWidth"
          [attr.y1]="padding"
          [attr.x2]="padding + chartWidth"
          [attr.y2]="height - padding"
          stroke="black"
          stroke-width="1"
          stroke-dasharray="5,5"
        opacity="0.5"></line>
        <!-- Draw grid lines for Y-axis -->
        @for (value of getYAxisValues(); track value) {
          <g>
            <line [attr.x1]="padding"
              [attr.y1]="getYAxisLabelY(value)"
              [attr.x2]="padding + chartWidth"
              [attr.y2]="getYAxisLabelY(value)"
              stroke="gray"
              stroke-width="1"
            opacity="0.3"></line>
          </g>
        }
        <!-- Draw line segments -->
        @for (segment of lineSegments; track segment) {
          <g>
            <!-- Visible line (2px) -->
            <line [attr.x1]="segment.startPoint.x"
              [attr.y1]="segment.startPoint.y"
              [attr.x2]="segment.endPoint.x"
              [attr.y2]="segment.endPoint.y"
              [attr.stroke]="segment.color"
              [attr.stroke-width]="2"
              [attr.opacity]="getOpacity(segment)"
            style="pointer-events: none;"></line>
            <!-- Invisible thicker line (5px) for easier mouse capture -->
            <line [attr.x1]="segment.startPoint.x"
              [attr.y1]="segment.startPoint.y"
              [attr.x2]="segment.endPoint.x"
              [attr.y2]="segment.endPoint.y"
              [attr.stroke]="'transparent'"
              [attr.stroke-width]="5"
              [attr.opacity]="0"
              (mouseenter)="showTooltipForSegment($event, segment)"
              (mouseleave)="hideTooltip()"
            style="cursor: pointer;"></line>
          </g>
        }
        <!-- Draw data points -->
        @for (point of dataPoints; track point) {
          <g>
            <circle [attr.cx]="point.x"
              [attr.cy]="point.y"
              [attr.r]="4"
              [attr.fill]="getColorForResponseCode(point.logEntries[0]?.responseCode || 200)"
              [attr.stroke]="'white'"
              [attr.stroke-width]="1"
              [attr.opacity]="getOpacity(point)"
              (mouseenter)="showTooltipForPoint($event, point)"
              (mouseleave)="hideTooltip()"
            style="cursor: pointer;"></circle>
          </g>
        }
        <!-- Draw labels on the right side -->
        @for (urlInfo of getAllUniqueUrls(); track urlInfo; let urlIndex = $index) {
          <g>
            <text [attr.x]="getRightLabelX()"
              [attr.y]="getRightLabelY(urlIndex, getAllUniqueUrls().length)"
              text-anchor="start"
              font-size="11"
              [attr.fill]="urlInfo.color"
              [attr.opacity]="getLabelOpacity(urlInfo)"
              font-weight="bold"
              (mouseenter)="onLabelMouseEnter(urlInfo)"
              (mouseleave)="onLabelMouseLeave()"
              style="cursor: pointer;">
              {{ getLabelText(urlInfo) }}
            </text>
          </g>
        }
        <!-- Draw X-axis labels -->
        @for (timeRange of allTimeRanges; track timeRange) {
          <ng-container>
            @if (shouldShowXAxisLabel(timeRange)) {
              <text
                [attr.x]="getXAxisLabelX(timeRange)"
                [attr.y]="height - padding + 20"
                text-anchor="middle"
                font-size="10"
                [attr.transform]="'rotate(-45, ' + getXAxisLabelX(timeRange) + ', ' + (height - padding + 20) + ') translate(0, 10)'">
                {{ timeRange }}
              </text>
            }
          </ng-container>
        }
        <!-- Draw Y-axis labels -->
        @for (value of getYAxisValues(); track value) {
          <text
            [attr.x]="padding - 10"
            [attr.y]="getYAxisLabelY(value) + 4"
            text-anchor="end"
            font-size="10">
            {{ value | number:'1.0-0' }}
          </text>
        }
        <!-- Axis labels -->
        <text [attr.x]="padding + chartWidth / 2"
          [attr.y]="height - 10"
          text-anchor="middle"
          font-size="12"
        font-weight="bold">Time Range</text>
        <text [attr.x]="20"
          [attr.y]="height / 2"
          text-anchor="middle"
          font-size="12"
          font-weight="bold"
        [attr.transform]="'rotate(-90, 20, ' + (height / 2) + ')'">Count</text>
      </svg>
      <!-- Tooltip -->
      @if (tooltip.visible) {
        <div
          class="tooltip"
          [style.left.px]="tooltip.x"
          [style.top.px]="tooltip.y"
          [style.transform]="tooltip.above ? 'translateY(-100%)' : 'none'">
          <div class="tooltip-content">{{ tooltip.text }}</div>
        </div>
      }
    </div>
  }
</div>
}


